import type { ComponentType } from 'react';
import GenderAll from 'svg/GenderAll';
import GenderCoed from 'svg/GenderCoed';
import GenderFemale from 'svg/GenderFemale';
import GenderMale from 'svg/GenderMale';
import GenderMixed from 'svg/GenderMixed';
import Users from 'svg/Users';
import { DetailSection } from 'components/Find/detail';
import type { FindGameAudience } from 'components/Find/types';

interface Props {
  audience: FindGameAudience;
  audienceLabel: string;
  description: string;
  /** Open Play's head count; the two-sided formats have none to show. */
  participantLimit?: number | null;
  /** Open Play with no head count. */
  isUnlimited?: boolean;
}

const AUDIENCE_ICON: Record<FindGameAudience, ComponentType<{ className?: string }>> = {
  ALL: GenderAll,
  MALE: GenderMale,
  FEMALE: GenderFemale,
  MIXED: GenderMixed,
  COED: GenderCoed,
};

export default function MatchDescription({
  audience,
  audienceLabel,
  description,
  participantLimit,
  isUnlimited = false,
}: Props) {
  const AudienceIcon = AUDIENCE_ICON[audience];

  return (
    <DetailSection title="Description" sizeDesktop="lg">
      <div className="flex flex-col gap-ds-sm">
        <div className="flex items-center gap-ds-sm">
          <AudienceIcon className="h-5 w-5 shrink-0 lg:h-6 lg:w-6" />
          <span className="typography-product-caption lg:typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            {audienceLabel}
          </span>
        </div>

        {(!!participantLimit || isUnlimited) && (
          <div className="flex items-center gap-ds-sm">
            <Users className="h-5 w-5 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon lg:h-6 lg:w-6" />
            <span className="typography-product-caption text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              <span className="lg:typography-product-body-highlight">Participant limit</span>{' '}
              <span className="text-color-brand-primary">•</span>{' '}
              <span className="font-semibold lg:font-bold">
                {isUnlimited ? 'Unlimited' : participantLimit}
              </span>
            </span>
          </div>
        )}

        <p className="typography-product-caption lg:typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
          {description}
        </p>
      </div>
    </DetailSection>
  );
}
